import { RelatedApplication } from 'models/related-application'

declare global {
  interface Navigator {
    getInstalledRelatedApps?: () => Promise<RelatedApplication[]>
  }
}

/**
 * Checks whether the device uses the Android operating system
 * @returns {boolean} true if it is an Android device false otherwise
 */
export const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent)
}

/**
 * Determines whether the current device is a mobile/touch device.
 * Returns true for standard mobile devices (Android, iPhone, iPad, iPod) and new iPads (macOS with touch support).
 * Returns false for desktop and other non-touch devices.
 *
 * @returns {boolean} - True if the device is mobile, false otherwise.
 */
export const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase()
  const isStandardMobile = /android|iphone|ipad|ipod/.test(userAgent)
  const isNewIPad = userAgent.includes('mac') && navigator.maxTouchPoints > 0

  return isStandardMobile || isNewIPad
}

/**
 * Checks if the app with the given ID is installed on the device.
 * This function uses the `navigator.getInstalledRelatedApps` API, which is supported in some browsers.
 * If the API is not available, it returns false.
 *
 * @param {string} appId - The ID of the app to check for installation.
 * @returns {Promise<boolean>} A promise that resolves to true if the app is installed, false otherwise.
 */
export const isAppInstalled = async (appId: string): Promise<boolean> => {
  if (!navigator.getInstalledRelatedApps) {
    return false
  }

  try {
    const relatedApps = await navigator.getInstalledRelatedApps()
    return relatedApps.some((app) => app.id === appId)
  } catch (error) {
    console.error(error)
    return false
  }
}
